import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { config } from '@/config';
import { logger } from '@/utils';

export interface UseWebSocketOptions {
  namespace?: string;
  autoConnect?: boolean;
  retryAttempts?: number;
  retryDelay?: number;
}

export interface UseWebSocketReturn {
  socket: Socket | null;
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  emit: (event: string, data?: any) => void;
}

export const useWebSocket = (options: UseWebSocketOptions = {}): UseWebSocketReturn => {
  const {
    namespace = '',
    autoConnect = true,
    retryAttempts = 3,
    retryDelay = 1000
  } = options;

  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();

  const connect = useCallback(async (): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        const socketUrl = `${config.websocket.url}${namespace}`;
        logger.info('Connecting to WebSocket', { url: socketUrl, namespace });

        // Parse URL to extract path prefix for proxy routing
        // Socket.IO interprets URL path as namespace, so we must use origin only
        // and put the proxy prefix in the path option
        const urlObj = new URL(socketUrl);
        const pathPrefix = urlObj.pathname.replace(/\/$/, '');
        const socketPath = pathPrefix ? `${pathPrefix}/socket.io` : '/socket.io';

        const newSocket = io(urlObj.origin, {
          path: socketPath,
          transports: ['websocket'],
          timeout: 20000,
          reconnection: false // We'll handle reconnection manually
        });

        newSocket.on('connect', () => {
          logger.info('WebSocket connected', { namespace });
          setIsConnected(true);
          retryCountRef.current = 0;
          resolve();
        });

        newSocket.on('disconnect', (reason) => {
          logger.info('WebSocket disconnected', { namespace, reason });
          setIsConnected(false);

          // Auto-retry connection if not manually disconnected
          if (reason !== 'io client disconnect' && retryCountRef.current < retryAttempts) {
            retryCountRef.current += 1;
            logger.info('Retrying WebSocket connection', {
              namespace,
              attempt: retryCountRef.current,
              maxAttempts: retryAttempts
            });

            retryTimeoutRef.current = setTimeout(() => {
              connect().catch((error) => {
                logger.error('WebSocket retry failed', { namespace, error });
              });
            }, retryDelay);
          }
        });

        newSocket.on('connect_error', (error) => {
          logger.error('WebSocket connection error', { namespace, error });
          setIsConnected(false);
          reject(error);
        });

        setSocket(newSocket);
      } catch (error) {
        logger.error('Failed to create WebSocket connection', { namespace, error });
        reject(error);
      }
    });
  }, [namespace, retryAttempts, retryDelay]);

  const disconnect = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    if (socket) {
      logger.info('Disconnecting WebSocket', { namespace });
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }
  }, [socket, namespace]);

  const emit = useCallback((event: string, data?: any) => {
    if (socket && isConnected) {
      socket.emit(event, data);
    } else {
      logger.warn('Cannot emit event: WebSocket not connected', { namespace, event });
    }
  }, [socket, isConnected, namespace]);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connect().catch((error) => {
        logger.error('Auto-connect failed', { namespace, error });
      });
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect, namespace]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      disconnect();
    };
  }, [disconnect]);

  return {
    socket,
    isConnected,
    connect,
    disconnect,
    emit
  };
};